"use client";

import React from "react";
import { Phone, Mail, Clock, CalendarCheck, CircleAlert, Loader, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function BookingForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-8 text-center space-y-4">
        <div className="size-12 rounded-md bg-primary/10 flex items-center justify-center mb-2">
          <CalendarCheck className="size-6 text-primary" />
        </div>
        <h3 className="text-xl font-default font-bold text-foreground uppercase tracking-wider">Planned Consultation</h3>
        <p className="text-muted-foreground font-default">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 rounded-md border-foreground font-default uppercase tracking-widest">
          New Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Minimum 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default uppercase text-xs font-bold tracking-widest">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="FULL NAME" className="rounded-md border-input focus:ring-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone is required",
        pattern: {
          value: /^[0-9\-\+\s\(\)]{10,15}$/,
          message: "Invalid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default uppercase text-xs font-bold tracking-widest">Phone</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="e.g., 918-000-0000" className="rounded-md border-input focus:ring-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Please describe your goals",
      minLength: {
        value: 10,
        message: "Minimum 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default uppercase text-xs font-bold tracking-widest">Performance Goals / Pain</FormLabel>
            <FormControl>
              <Textarea placeholder="Briefly describe your injury or performance goals..." className="min-h-[100px] resize-y rounded-md border-input focus:ring-primary" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md flex items-start gap-2 text-sm text-destructive">
          <CircleAlert className="size-4 mt-0.5 shrink-0" />
          <p>{submitError}</p>
        </div>}

      <Button type="submit" size="lg" disabled={isSubmitting} className="w-full text-base h-12 mt-2 rounded-md bg-primary text-primary-foreground font-default font-bold uppercase tracking-widest hover:opacity-90 transition-opacity">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            PROCESSING...
          </> : <>
            START RECOVERY
            <ArrowRight className="ml-2 size-5" />
          </>}
      </Button>
      <p className="text-[10px] text-center text-muted-foreground mt-3 uppercase tracking-widest">
        Medical confidentiality guaranteed.
      </p>
    </Form>;
}
export default function StartYourRecovery() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="254754" id="start-your-recovery" ref={sectionRef} className="bg-muted text-foreground py-16 md:py-24 relative overflow-hidden border-b-4 border-primary">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-5">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-md bg-primary blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-md bg-primary blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
          
          {}
          <div className={`space-y-8 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="space-y-4">
              <Badge variant="default" className="bg-primary text-primary-foreground border-none px-3 py-1 text-xs font-default font-bold uppercase tracking-widest rounded-md">Evaluations in 24H</Badge>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold leading-tight uppercase tracking-tighter">Start Moving Pain-Free Again<br /></h2>
              <p className="text-foreground/80 text-lg md:text-xl max-w-md font-default"></p>
            </div>

            <div className="bg-background border border-border rounded-md p-6 space-y-6">
              <div className="w-12 h-px bg-primary mb-2" />
              <h3 className="font-default font-bold text-xl flex items-center gap-2 uppercase tracking-wider">
                <CircleAlert className="text-primary size-5" />
                TULSA, OKLAHOMA
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="bg-primary/10 p-3 rounded-md shrink-0">
                    <Phone className="size-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-0.5">Phone Number</p>
                    <Link to="tel:+19185550199" className="text-xl font-default font-bold hover:text-primary transition-colors">
                      (918) 555-0199
                    </Link>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="bg-primary/10 p-3 rounded-md shrink-0">
                    <Mail className="size-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-0.5">Email&nbsp;</p>
                    <Link to="mailto:performance@tulsapain.com" className="text-lg font-default font-medium hover:text-primary transition-colors">
                      performance@tulsapain.com
                    </Link>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="bg-primary/10 p-3 rounded-md shrink-0">
                    <Clock className="size-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-0.5">Hours</p>
                    <p className="text-lg font-default font-medium">9am-7pm</p>
                  </div>
                </div>
              </div>
              <div className="w-full h-px bg-primary/10 mt-4" />
            </div>
          </div>

          {}
          <div className={`transition-all duration-700 delay-200 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Card className="border border-border shadow-lg bg-background text-foreground rounded-md overflow-hidden">
              <CardHeader className="pb-4 border-b border-border bg-muted/50">
                <CardTitle className="text-2xl font-default font-bold uppercase tracking-wider">Take Action</CardTitle>
                <CardDescription className="text-base font-default">Fill out the form and we'll reach out to you within the hour.</CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <FormProvider formKey="start_recovery_form_home" formId="start_recovery_form_home" sectionName="contact-cta" defaultValues={{
                name: "",
                phone: "",
                message: ""
              }} submitText="START RECOVERY" successMessage="Your request has been sent to our clinical team. A specialist will contact you shortly.">
                  <BookingForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}